/**
 * Turn a slug-ish skill name into a human title.
 *   cv-expat-arab      → "CV Expat Arab"
 *   li-post            → "LinkedIn Post"
 *   cv-ats             → "CV ATS"
 * Names that already read as human (contain a space or camelCase) pass through.
 */
const ACRONYMS: Record<string, string> = {
  cv: 'CV',
  ats: 'ATS',
  gcc: 'GCC',
  uae: 'UAE',
  ksa: 'KSA',
  dha: 'DHA',
  doh: 'DOH',
  moh: 'MOH',
  scfhs: 'SCFHS',
  hr: 'HR',
  dm: 'DM',
  ai: 'AI',
  li: 'LinkedIn',
  linkedin: 'LinkedIn',
  pdpl: 'PDPL',
  star: 'STAR',
  pm: 'PM',
  us: 'US',
  uk: 'UK',
  eu: 'EU',
};

export function prettySkillName(name: string): string {
  if (!name) return name;
  // Already human (has whitespace, or a lowercase-then-uppercase boundary).
  if (/\s/.test(name) || /[a-z][A-Z]/.test(name)) return name;
  return name
    .split(/[-_]+/)
    .filter(Boolean)
    .map(
      (w) => ACRONYMS[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(' ');
}

/** Canonical chat command. Never derive this from a localized display name. */
export function formatSkillCommand(skillId: string): string {
  return `/${skillId.trim().replace(/^\/+/, '').toLowerCase()}`;
}

/**
 * Compact count formatter for a skill's social-proof number. Named for the
 * count generally, not for installs: the hub shows `usageCount` (runs), which
 * is the number that actually moves - a skill is used straight from the
 * catalog row and from chat, and neither installs anything.
 */
export function formatSkillCount(
  count: number | null | undefined,
  compact = false,
): string {
  const safeCount =
    typeof count === 'number' && Number.isFinite(count)
      ? Math.max(0, count)
      : 0;
  if (!compact) return new Intl.NumberFormat().format(safeCount);
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(safeCount);
}
